import { GraduationCap, Factory, Sparkles, Heart, Leaf, Award, Truck, Shield, Star, Zap } from "lucide-react";
import { useContent, t, parseHiddenSet } from "@/hooks/useContent";
import Seo from "@/components/Seo";

const ICON_MAP = {
  graduationcap: <GraduationCap />, factory: <Factory />, sparkles: <Sparkles />, heart: <Heart />,
  leaf: <Leaf />, award: <Award />, truck: <Truck />,
  shield: <Shield />, star: <Star />, zap: <Zap />,
};

function iconFor(name) {
  return ICON_MAP[String(name || "").toLowerCase()] || <Sparkles />;
}

function parseMilestones(raw) {
  const fallback = [
    { icon: "graduationcap", title: "BTech, Food Engineering", text: "Formal training in food chemistry, safety, and processing." },
    { icon: "factory", title: "9+ Years in Industry", text: "Across R&D, quality, and production — from lab to line." },
    { icon: "sparkles", title: "Left Corporate Life", text: "Walked away from a secure job to build B-Raw full-time — driven by pure passion." },
    { icon: "heart", title: "Community-first", text: "Small-batch, honest pricing, and hand-crafted quality in every pack." },
  ];
  if (!raw) return fallback;
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return fallback;
    return arr.map((m) => ({ icon: m?.icon || "sparkles", title: m?.title || "", text: m?.text || "", hidden: !!m?.hidden }));
  } catch { return fallback; }
}

export default function About() {
  const c = useContent();
  const hidden = parseHiddenSet(c);
  const H = (id) => hidden.has(id);
  const milestones = parseMilestones(t(c, "about.milestones", "")).filter((m) => !m.hidden);
  return (
    <div>
      <Seo
        path="/about"
        title="About B-Raw — Founder Devender Yadav, BTech Food Engineer"
        description="B-Raw is founded by Devender Yadav, a BTech in Food Engineering with 9+ years in India's food industry. Hand-crafted, FSSAI-certified snacks made in Sirsa, Haryana."
      />
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20" data-testid="about-section">
        {!H("about.story.section") && (
          <>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D9381E]">
              Our story
            </div>
            {!H("about.title") && (
              <h1 className="mt-2 font-display font-black text-5xl sm:text-6xl tracking-tighter text-[#111]">
                {t(c, "about.title", "A food engineer with one honest goal.")}
              </h1>
            )}
            <div className="mt-6 space-y-5 text-[#4B5563] text-base md:text-lg leading-relaxed">
              {!H("about.para_1") && <p>{t(c, "about.para_1", "B-Raw was born in the small workshops of Sirsa, Haryana — where founder Devender Yadav spent years perfecting recipes he'd grown up eating.")}</p>}
              {!H("about.para_2") && <p>{t(c, "about.para_2", "After earning a BTech in Food Engineering and spending 9+ years across India's food industry — from R&D labs to production floors — Devender saw one gap clearly: everyday snacks priced at ₹5 rarely respected the people eating them.")}</p>}
              {!H("about.para_3") && <p>{t(c, "about.para_3", "So he did what took real courage — he walked away from a secure corporate career and poured every ounce of that experience into his own workshop. B-Raw isn't a side project — it's a full-time act of passion, built to prove that a ₹5 pouch can still be honest, hand-crafted, and worth loving.")}</p>}
              {!H("about.para_4") && <p>{t(c, "about.para_4", "Every pouch is built on food science, not shortcuts — real ingredients, transparent labels, and small-batch, FSSAI-certified quality you can taste.")}</p>}
            </div>
          </>
        )}

        {!H("about.milestones.section") && milestones.length > 0 && (
          <div className="mt-10 grid sm:grid-cols-2 gap-4" data-testid="about-milestones">
            {milestones.map((m, i) => (
              <Milestone key={i} icon={iconFor(m.icon)} title={m.title} text={m.text} />
            ))}
          </div>
        )}
      </section>

      {!H("about.promise.section") && (
      <section className="bg-[#111] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          {!H("about.promise_eyebrow") && (
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F2A900]">
              {t(c, "about.promise_eyebrow", "Our promise")}
            </div>
          )}
          {!H("about.promise") && (
            <p className="mt-4 font-display text-3xl sm:text-4xl leading-tight">
              &ldquo;{t(c, "about.promise", "No shortcuts on ingredients. No fine print. Just hand-crafted, FSSAI-certified snacks at an honest price — small-batch, made-with-love.")}&rdquo;
            </p>
          )}
          {!H("about.promise_by") && (
            <div className="mt-6 text-white/60 text-sm">{t(c, "about.promise_by", "— Devender Yadav, Founder")}</div>
          )}

          {(() => {
            let pillars = [];
            const raw = t(c, "about.promise_pillars", "[]");
            try {
              const arr = JSON.parse(raw);
              if (Array.isArray(arr)) pillars = arr
                .map((p) => ({ icon: p?.icon || "sparkles", title: p?.title || "", text: p?.text || "", hidden: !!p?.hidden }))
                .filter((p) => !p.hidden);
            } catch {}
            if (!pillars.length) return null;
            return (
              <div className="mt-12 grid gap-6 sm:grid-cols-2 md:grid-cols-3 text-left" data-testid="about-promise-pillars">
                {pillars.map((p, i) => (
                  <div key={i} className="p-5 bg-white/5 border border-white/10 rounded-2xl">
                    <div className="h-10 w-10 rounded-lg bg-[#F2A900] text-[#111] flex items-center justify-center">
                      {iconFor(p.icon)}
                    </div>
                    <div className="mt-3 font-display font-bold text-lg text-white">{p.title}</div>
                    <p className="mt-1 text-sm text-white/70 leading-relaxed">{p.text}</p>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </section>
      )}
    </div>
  );
}

function Milestone({ icon, title, text }) {
  return (
    <div className="p-5 bg-white rounded-2xl border border-black/10">
      <div className="h-10 w-10 rounded-lg bg-[#D9381E] text-white flex items-center justify-center">
        {icon}
      </div>
      <div className="mt-3 font-display font-bold text-lg text-[#111]">{title}</div>
      <p className="mt-1 text-sm text-[#4B5563]">{text}</p>
    </div>
  );
}
